import { useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { SelectedOption, SelectedOptionValue } from 'react-select-search';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'core';
import {
  clearPreparationCentre,
  selectPreparationCentre,
  selectSelectedCountryId,
  setCountry,
} from 'store/country/countrySlice';
import { selectBasketExamsNumber } from 'store/basket/basketSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetCountriesQuery, useLazyGetOrganisationCountryConfigQuery } from 'store/country/countryAPI';

import { Button } from '@britishcouncil/react-solas';
import { ApiErrorPage, BcSelect, Divider, PageContainer, PageLoader, Quote } from '@britishcouncil/react-solas-ors3';

import { CountrySelectOption } from './components';
import { useCountrySelectionFromUri } from './useCountrySelectionFromUri';

export function CountrySelector() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const storedCountryId = useAppSelector(selectSelectedCountryId);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const examsInBasketCount = useAppSelector(selectBasketExamsNumber);
  const [countryId, setCountryId] = useState(storedCountryId);
  const selectedCountryIsSameAsStored = storedCountryId === countryId;
  const showBasketWarning = !selectedCountryIsSameAsStored && examsInBasketCount > 0;
  const { data, error, isLoading: isCountriesLoading } = useGetCountriesQuery();

  const [
    getOrganisationCountryConfig,
    {
      data: organisationCountryConfig,
      isFetching: organisationCountryConfigLoading,
      isSuccess: organisationCountryConfigLoaded,
    },
  ] = useLazyGetOrganisationCountryConfigQuery();

  useEffect(() => {
    if (countryId && organisationCountryConfigLoaded && organisationCountryConfig) {
      dispatch(setCountry({ ...organisationCountryConfig, id: Number(countryId) }));
      navigate(ROUTES.EXAMS);
    }
  }, [organisationCountryConfigLoaded, organisationCountryConfig, countryId, dispatch]);

  const handleCountryChange = (_selected: SelectedOptionValue, country: SelectedOption) => {
    setCountryId(country.value);
  };

  const handleCountrySelect = useCallback(
    (countryId?: string) => {
      if (!countryId) {
        return;
      }

      setCountryId(countryId);
      if (storedCountryId === countryId) {
        navigate(ROUTES.EXAMS);
      } else {
        getOrganisationCountryConfig(Number(countryId));
      }
    },
    [getOrganisationCountryConfig, storedCountryId]
  );

  const handlePreparationCentreClear = () => {
    dispatch(clearPreparationCentre());
  };

  const isSelectionFromUriLoading = useCountrySelectionFromUri(handleCountrySelect);
  const isLoading = isCountriesLoading || isSelectionFromUriLoading;

  return (
    <PageContainer>
      <h2>Welcome to the British Council school exams registration site</h2>
      <p>To get started please select your country/territory from the list below</p>
      <Quote title="Notice" intent="warning">
        The British Council takes no responsibility for what subjects/papers candidates register for. Candidates are
        held responsible for the subjects/pages they choose to register for.
      </Quote>

      {error && <ApiErrorPage />}
      {isLoading && <PageLoader />}
      {data && !isLoading && (
        <StyledWrapper>
          {showBasketWarning && (
            <Quote title="Warning" intent="warning">
              Country/Territory change will remove already added exams to your basket
            </Quote>
          )}
          <BcSelect
            options={data}
            value={countryId}
            onChange={handleCountryChange}
            placeholder="Select country/territory"
            renderOption={CountrySelectOption}
          />
          <Button
            intent="default"
            disabled={!countryId || organisationCountryConfigLoading}
            onClick={() => handleCountrySelect(countryId)}
            loading={organisationCountryConfigLoading}
          >
            Continue
          </Button>
          <StyledHelp>Can't find your country/territory?</StyledHelp>
          <p>
            If your country/territory does not appear on the list, please visit the British Council website to find out
            more about available exams in your country/territory.
          </p>
          <p>
            <a
              href="https://www.britishcouncil.org/exam/international-schools"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.britishcouncil.org/exam/international-schools
            </a>
          </p>

          {pcInfo && (
            <>
              <Divider />
              <p>
                You have opened the application via school-specific link. Use button below to reset school selection and
                be able to select location and schools
              </p>
              <Button intent="default" onClick={handlePreparationCentreClear}>
                Clear pre-selected school
              </Button>
            </>
          )}
        </StyledWrapper>
      )}
    </PageContainer>
  );
}

const StyledHelp = styled.h3`
  margin-top: 25px;
`;
const StyledWrapper = styled.div`
  display: contents;

  .select-search {
    margin-bottom: 25px;
  }
`;
